import { SeriesPoint, stack, stackOffsetExpand, stackOffsetNone } from 'd3-shape';
import { useMemo } from 'react';

import { DataKeyGetterProp, DataKeyType } from '../utils/DataProps';
import { NumberCoercible } from '../utils/NumberCoercible';
import { PointInfo } from '../utils/PointInfo';
import { PartialSeriesPoint, Series } from '../utils/Series';
import { DataByXValue } from './useDataByXValue';

interface UseGeneratorOutputParams<TDatum, TDataKeyType extends DataKeyType, TPointType, TXValueType>
  extends DataKeyGetterProp<TDatum, TDataKeyType> {
  /**
   * The chart data.
   */
  data: readonly Readonly<TDatum>[];
  /**
   * The chart data, indexed by x-value.
   */
  dataByXValue: DataByXValue<TDataKeyType, TPointType, TDatum, TXValueType>;
  /**
   * Whether to apply the expand stack offset, normalizing values from [0, 1].
   */
  expand: boolean;
  /**
   * Whether the generated data should be stacked.
   */
  stacked: boolean;
  /**
   * If stacked, whether the stack should be reversed.
   */
  reverseStack: boolean;
}

export type GeneratorOutputPoint<TDataKeyType, TPointType, TDatum, TXValueType> = PartialSeriesPoint<
  [x: TXValueType, yValuesMap: Map<TDataKeyType, PointInfo<TPointType, TDatum, NumberCoercible | undefined>>]
>;
export type GeneratorOutput<TDataKeyType, TPointType, TDatum, TXValueType = NumberCoercible> = Series<
  GeneratorOutputPoint<TDataKeyType, TPointType, TDatum, TXValueType>,
  TDataKeyType
>[];

export interface FilteredGeneratorOutput<TDataKeyType, TPointType, TDatum, TXValueType = NumberCoercible> {
  /**
   * The data key of the series associated with these points.
   */
  seriesDataKey: TDataKeyType;
  /**
   * The generated points, with synthetic points (i.e., generated points based on points that were
   * not defined in the original data set) omitted.
   */
  generatedPoints: SeriesPoint<
    [x: TXValueType, yValuesMap: Map<TDataKeyType, PointInfo<TPointType, TDatum, NumberCoercible | undefined>>]
  >[];
}

interface UseGeneratorOutputReturnType<TDataKeyType extends DataKeyType, TPointType, TDatum, TXValueType> {
  /**
   * The generator output.
   */
  generatorOutput: GeneratorOutput<TDataKeyType, TPointType, TDatum, TXValueType>;
  /**
   * The generator output with additional metadata and with synthetic points (i.e., generated points
   * based on points that were not defined in the original data set) omitted.
   */
  filteredGeneratorOutput: FilteredGeneratorOutput<TDataKeyType, TPointType, TDatum, TXValueType>[];
}

/**
 * Returns processed chart data as returned by a generator.
 */
export const useGeneratorOutput = <
  TDataKeyType extends DataKeyType,
  TPointType,
  TDatum,
  TXValueType = NumberCoercible,
>({
  data,
  dataByXValue,
  dataKeyGetter,
  expand,
  reverseStack,
  stacked,
}: UseGeneratorOutputParams<TDatum, TDataKeyType, TPointType, TXValueType>): UseGeneratorOutputReturnType<
  TDataKeyType,
  TPointType,
  TDatum,
  TXValueType
> => {
  const generatorOutput = useMemo<GeneratorOutput<TDataKeyType, TPointType, TDatum, TXValueType>>(() => {
    const getYValue = ([, map]: typeof dataByXValue[number], key: TDataKeyType) => map.get(key)?.value?.valueOf();

    if (stacked) {
      const keys = data.map(dataKeyGetter);
      const orderedKeys = reverseStack ? keys.reverse() : keys;

      const stackGenerator = stack<typeof dataByXValue[number], TDataKeyType>()
        .keys(orderedKeys)
        .value((v, key) => {
          return getYValue(v, key) ?? 0;
        })
        .offset(expand ? stackOffsetExpand : stackOffsetNone);
      return stackGenerator(dataByXValue);
    }

    // This is effectively the output of a stack generator whose y0 coordinates are always zero, and
    // whose y1 coordinates are always the series' value (not reflecting the sum of other series).
    return data.map(dataKeyGetter).map((dataKey, i) => {
      const values = dataByXValue.map((row) => {
        const point = [0, getYValue(row, dataKey)] as const;
        return Object.assign(point, { data: row });
      });

      return Object.assign(values, { key: dataKey, index: i });
    });
  }, [data, dataByXValue, dataKeyGetter, expand, reverseStack, stacked]);

  const filteredGeneratorOutput = useMemo(() => {
    return generatorOutput.map((generatorSeries) => {
      const seriesDataKey = generatorSeries.key;

      const generatedPoints = generatorSeries.filter(
        (
          generatedPoint,
        ): generatedPoint is SeriesPoint<
          [x: TXValueType, yValuesMap: Map<TDataKeyType, PointInfo<TPointType, TDatum, NumberCoercible | undefined>>]
        > => {
          // The generator may have added synthetic points where there were gaps or undefined values
          // in the original data series. Filter out any synthetic points here.
          const originalValue = generatedPoint.data[1].get(seriesDataKey)?.value;
          if (originalValue === undefined) {
            return false;
          }

          const y = generatedPoint[1];
          return y !== undefined;
        },
      );

      return {
        seriesDataKey,
        generatedPoints,
      };
    });
  }, [generatorOutput]);

  return { generatorOutput, filteredGeneratorOutput };
};
